import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Users, Map as MapIcon, MapPin, Store, UserCheck } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  useConfiguracionFidelizacion,
  useMetadataFidelizacion,
  useResumenFidelizacion,
  useTodosParticipantesFidelizacion,
} from '../hooks/useFidelizacion'
import { DynamicFiltersBar, type DynamicFiltersValue, type FieldOption } from '../components/DynamicFiltersBar'
import { useCascadaRepresentante } from '../hooks/useCascadaRepresentante'
import { parseFidelizacionError } from '../hooks/useFidelizacionError'
import { normalizeColor } from '../lib/reporteColor'
import { mergeCadenasPorNombre } from '../lib/cadenas'
import { nombreDistritoReporte } from '../lib/distritosMedifarmaPeru'

// created_at llega como ISO UTC ("2026-09-01T04:00:00Z"). Slicear directamente
// daría la fecha UTC, que para Perú (UTC-5) puede ser un día adelante. Se
// convierte primero al timezone local del programa.
const TZ_PERU = 'America/Lima'
function toLocalDate(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso.slice(0, 10)
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ_PERU, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
}
import {
  CAMPO_CADENA_CANDIDATES,
  CAMPO_REPRESENTANTE_CANDIDATES,
  autoFieldOptions,
  collectPairs,
  parseMulti,
  participantePairs,
  pickCampo,
  pickCampoFiltrable,
} from '../lib/campos'
import { ReporteHeader } from '../components/ReporteHeader'
import { ReporteSubNav } from '../components/ReporteSubNav'
import { usePeriodoReporte } from '../hooks/usePeriodoReporte'
import { ReportePeriodoBar } from '../components/ReportePeriodoBar'
import { StatCardConsolidado } from '../components/StatCardConsolidado'
import { ListadoTab } from '../components/ListadoTab'
import { DynamicDataTable } from '../components/DynamicDataTable'
import type { PaginatedListado } from '../types'
import { BarChartLabelLeft } from '../components/charts/BarChartLabelLeft'
import { BarChartAgrupado, type DatasetOpcion } from '../components/charts/BarChartAgrupado'
import { AreaChartFecha } from '../components/charts/AreaChartFecha'
import { MapaRegiones } from '../components/charts/MapaRegiones'
import { GEOJSON_DISTRITOS, GEOJSON_PAIS, insetPais, regionTermino } from '../lib/paisMapa'

/** Cuenta ocurrencias por etiqueta y devuelve [{label,value}] ordenado desc. */
function agruparConteo(
  items: Array<Record<string, unknown>>,
  getLabel: (p: Record<string, unknown>) => unknown,
): Array<{ label: string; value: number }> {
  const m = new Map<string, number>()
  for (const p of items) {
    const raw = getLabel(p)
    if (raw == null || String(raw).trim() === '') continue
    const label = String(raw)
    m.set(label, (m.get(label) ?? 0) + 1)
  }
  return Array.from(m.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
}

/** Normaliza el flag "email_verificado" (bool/str/num) del participante a booleano. */
function esVerificado(p: Record<string, unknown>): boolean {
  const extra = (p.datos_extra as Record<string, unknown> | undefined) ?? {}
  const v = extra.email_verificado ?? p.email_verificado
  return v === true || v === 'true' || v === 1 || v === '1'
}

/** Puntos disponibles del participante (llegan como string) → número. */
const puntosDe = (p: Record<string, unknown>): number =>
  parseFloat(String(p.puntos_disponibles ?? '0')) || 0

/**
 * Filtro "Puntos" (Medifarma Perú, id 7): distingue participantes con puntos
 * (`puntos_disponibles > 0`) de los que no tienen (`≤ 0`). Es un concepto distinto
 * del flag `activo` del upstream. `puntos_disponibles` no es filtrable en el upstream,
 * por lo que este filtro se resuelve client-side sobre el listado completo.
 */
type FiltroPuntos = 'todos' | 'con' | 'sin'

export function RegistrosFidelizacionPage() {
  const { id } = useParams<{ id: string }>()
  const configId = id ? parseInt(id, 10) : 0

  const config = useConfiguracionFidelizacion(configId)
  const metadata = useMetadataFidelizacion(configId)

  // "Todo el histórico": al activarse se omite el rango de fechas y todas las
  // métricas se calculan sobre el total de datos del programa. `modo` alterna
  // entre selector de mes y rango de fechas personalizado.
  const {
    mes,
    setMes,
    sinFecha,
    setSinFecha,
    modo,
    setModo,
    rangoPersonalizado,
    setRangoPersonalizado,
    rango,
    rangoEfectivo,
  } = usePeriodoReporte()

  // Filtros globales del reporte (provincia / cadena / representante / etc.).
  // Se aplican a TODAS las métricas + la tabla. Compartidos vía estado del padre
  // para que cards, charts y listado respondan al mismo set de filtros.
  const [filtros, setFiltros] = useState<DynamicFiltersValue>({})

  // Filtro "Puntos" (id 7): 'todos' | 'con' (>0) | 'sin' (≤0). Client-side.
  const [filtroPuntos, setFiltroPuntos] = useState<FiltroPuntos>('todos')
  // Página de la tabla client-side (sólo cuando filtroPuntos != 'todos').
  const [pageCS, setPageCS] = useState(1)

  // Nivel geográfico del mapa. Se alterna con un botón en la cabecera del mapa
  // (departamentos ↔ distritos); el nivel distrito solo aplica a Medifarma Perú.
  const [mostrarDistritos, setMostrarDistritos] = useState(false)

  // Listado completo de participantes para extraer pares ID/nombre de provincia,
  // cadena y representante. Los endpoints de métricas (`/metricas/por/{campo}`)
  // sólo devuelven el nombre resuelto, pero el upstream filtra por ID — por lo
  // que necesitamos los pares para que el dropdown muestre nombre y envíe ID.
  const todosParticipantes = useTodosParticipantesFidelizacion(configId)

  const camposExternos = metadata.data?.campos_externos
  const campoCadena = useMemo(() => pickCampo(camposExternos, CAMPO_CADENA_CANDIDATES), [camposExternos])
  const campoRepresentante = useMemo(
    () => pickCampo(camposExternos, CAMPO_REPRESENTANTE_CANDIDATES),
    [camposExternos],
  )

  // Campo geográfico fino (upstream `ciudad` → distrito en Medifarma Perú).
  const campoCiudad = useMemo(() => pickCampo(camposExternos, ['ciudad', 'distrito']), [camposExternos])

  // Campos para poblar los dropdowns de los filtros de la tabla. Se buscan por
  // `filtrable` (independiente de `agrupable`).
  const campoCadenaFiltro = useMemo(
    () => pickCampoFiltrable(camposExternos, CAMPO_CADENA_CANDIDATES),
    [camposExternos],
  )
  const campoRepFiltro = useMemo(
    () => pickCampoFiltrable(camposExternos, CAMPO_REPRESENTANTE_CANDIDATES),
    [camposExternos],
  )

  // Cascada Jefe de Ventas / Representante Médico (Medifarma Perú, id 7): inyecta
  // el conjunto de representantes del jefe en el filtro que va al upstream.
  const { cascadeSelects, inyectarReps, repSetCliente, narrowRepOptions } = useCascadaRepresentante({
    configId,
    participantes: todosParticipantes.data,
    repKey: campoRepFiltro?.key,
    filtros,
    setFiltros,
  })
  // Inyección del conjunto de representantes para las superficies server-side
  // (tabla + resumen). Las gráficas/mapa se filtran client-side con `repSetCliente`.
  const filtrosInyectados = useMemo(
    () => inyectarReps(filtros),
    [filtros, inyectarReps],
  )
  const filtrosCompletos = useMemo(
    () => ({ ...rangoEfectivo, ...filtrosInyectados }),
    [rangoEfectivo, filtrosInyectados],
  )

  const resumen = useResumenFidelizacion(configId, filtrosCompletos)

  // Predicado client-side: participante cuyo representante pertenece al
  // jefe/supervisor elegido. Aplica a las gráficas/mapa derivadas del listado.
  const pasaCascadaReps = useCallback(
    (p: Record<string, unknown>) => {
      const set = repSetCliente
      if (!set) return true
      const k = campoRepFiltro?.key
      const repId = k ? (p.datos_extra as Record<string, unknown> | undefined)?.[k] : undefined
      return set.has(String(repId ?? ''))
    },
    [repSetCliente, campoRepFiltro?.key],
  )

  // ── Filtro "Puntos" (Medifarma Perú, id 7) ─────────────────────────────────
  // Predicado client-side por puntos disponibles. Cuando está activo (≠ 'todos')
  // se deriva todo del listado completo (como el filtro Verificado).
  const puntosActivo = configId === 7 && filtroPuntos !== 'todos'
  const pasaPuntos = useCallback(
    (p: Record<string, unknown>) => {
      if (filtroPuntos === 'todos') return true
      const pts = puntosDe(p)
      return filtroPuntos === 'con' ? pts > 0 : pts <= 0
    },
    [filtroPuntos],
  )
  // Cambiar el filtro por puntos reinicia la paginación client-side.
  const cambiarFiltroPuntos = useCallback((v: FiltroPuntos) => {
    setFiltroPuntos(v)
    setPageCS(1)
  }, [])
  // Cambiar el resto de filtros también reinicia la paginación client-side.
  const handleFiltrosChange = useCallback((next: DynamicFiltersValue) => {
    setFiltros(next)
    setPageCS(1)
  }, [])

  // ── Filtro "Verificado" (Medifarma Perú, id 7) ──────────────────────────────
  // El upstream sólo aplica `validado` en el listado de participantes; sus
  // endpoints de agregación (resumen / por-provincia / por/{campo} / serie) lo
  // ignoran silenciosamente. Cuando el filtro está activo derivamos KPIs,
  // gráficas y mapa del listado completo ya cargado (`todosParticipantes`),
  // aplicando client-side TODOS los filtros activos para que combinen bien
  // (provincia + representante + rango de fechas + verificado).
  const validadoActivo =
    configId === 7 && (filtros.validado === 'true' || filtros.validado === 'false')

  // Cascada por conjunto de representantes activa (jefe/supervisor sin rep puntual):
  // el upstream no filtra `resumen`/agregados por un conjunto de IDs, así que los
  // KPIs se derivan client-side del listado completo (igual que el filtro Verificado).
  const cascadaClientSide = repSetCliente != null
  // Dependiente (participante puntual): el upstream tampoco filtra los agregados
  // por `participante_id`, así que sus KPIs se derivan client-side del listado.
  const dependienteActivo = configId === 7 && Boolean(filtros.participante_id)
  const derivarClientSide = validadoActivo || cascadaClientSide || dependienteActivo || puntosActivo

  const participantesFiltrados = useMemo(() => {
    if (!validadoActivo && !cascadaClientSide && !dependienteActivo && !puntosActivo) return []
    const verifDeseado = filtros.validado === 'true'
    const { desde, hasta } = rangoEfectivo
    return (todosParticipantes.data ?? []).filter((p) => {
      if (validadoActivo && esVerificado(p) !== verifDeseado) return false
      if (!pasaPuntos(p)) return false
      if (!pasaCascadaReps(p)) return false
      // Rango de fechas sobre created_at (omitido en "todo el histórico").
      if (desde || hasta) {
        const f = p.created_at ? toLocalDate(String(p.created_at)) : ''
        if (desde && f < desde) return false
        if (hasta && f > hasta) return false
      }
      // Resto de filtros activos: `provincia` es top-level; el resto vive en
      // `datos_extra` (representante y cualquier otro campo dinámico filtrable).
      const extra = (p.datos_extra as Record<string, unknown> | undefined) ?? {}
      for (const [k, val] of Object.entries(filtros)) {
        if (!val || k === 'validado') continue
        const campoVal = k === 'provincia' ? p.provincia : k === 'participante_id' ? p.id : extra[k]
        // Filtro multi-selección (coma-separado): pasa si el valor del campo está
        // en el conjunto elegido.
        if (!parseMulti(val).includes(String(campoVal ?? ''))) return false
      }
      return true
    })
  }, [validadoActivo, cascadaClientSide, dependienteActivo, puntosActivo, todosParticipantes.data, filtros, rangoEfectivo, pasaCascadaReps, pasaPuntos])

  // Para el chart de fechas y el KPI "nuevos": siempre usar todosParticipantes
  // con toLocalDate() para evitar el bug UTC del endpoint serie-temporal.
  const participantesPorFecha = useMemo(() => {
    const { desde, hasta } = rangoEfectivo
    return (todosParticipantes.data ?? []).filter((p) => {
      if (validadoActivo) {
        const verifDeseado = filtros.validado === 'true'
        if (esVerificado(p) !== verifDeseado) return false
      }
      if (!pasaPuntos(p)) return false
      if (!pasaCascadaReps(p)) return false
      if (desde || hasta) {
        const f = p.created_at ? toLocalDate(String(p.created_at)) : ''
        if (desde && f < desde) return false
        if (hasta && f > hasta) return false
      }
      const extra = (p.datos_extra as Record<string, unknown> | undefined) ?? {}
      for (const [k, val] of Object.entries(filtros)) {
        if (!val || k === 'validado') continue
        const campoVal = k === 'provincia' ? p.provincia : k === 'participante_id' ? p.id : extra[k]
        // Filtro multi-selección (coma-separado): pasa si el valor del campo está
        // en el conjunto elegido.
        if (!parseMulti(val).includes(String(campoVal ?? ''))) return false
      }
      return true
    })
  }, [todosParticipantes.data, filtros, rangoEfectivo, validadoActivo, pasaCascadaReps, pasaPuntos])


  const color = normalizeColor(config.data)
  const errorGlobal = config.error || metadata.error || resumen.error

  const handleRefresh = () => {
    metadata.refetch()
    resumen.refetch()
    todosParticipantes.refetch()
  }

  const refreshing =
    metadata.isFetching ||
    resumen.isFetching ||
    todosParticipantes.isFetching

  // Participantes filtrados client-side. Sólo se usa para calcular los
// Datos para barras y mapa: vienen directo del upstream que ya filtra
  // por todos los criterios (provincia + datos_extra dinámicos).
  const provinciaData = useMemo(
    () => agruparConteo(participantesPorFecha, (p) => p.provincia_nombre),
    [participantesPorFecha],
  )
  const cadenaData = useMemo(
    () =>
      mergeCadenasPorNombre(
        agruparConteo(participantesPorFecha, (p) => {
          const e = (p.datos_extra as Record<string, unknown> | undefined) ?? {}
          const k = campoCadena?.key
          return k ? e[`${k}_nombre`] ?? e[k] : null
        }),
      ),
    [participantesPorFecha, campoCadena?.key],
  )
  const repData = useMemo(
    () =>
      agruparConteo(participantesPorFecha, (p) => {
        const e = (p.datos_extra as Record<string, unknown> | undefined) ?? {}
        const k = campoRepresentante?.key
        return k ? e[`${k}_nombre`] ?? e[k] : null
      }),
    [participantesPorFecha, campoRepresentante?.key],
  )
  // El upstream de Medifarma Perú devuelve el ID de la ciudad/distrito en
  // `valor` (no el nombre). Lo resolvemos con el catálogo local id → nombre.
  const ciudadData = useMemo(
    () =>
      agruparConteo(participantesPorFecha, (p) => {
        const e = (p.datos_extra as Record<string, unknown> | undefined) ?? {}
        const k = campoCiudad?.key
        const id = k ? e[k] : undefined
        return id != null ? nombreDistritoReporte(configId, String(id)) ?? String(id) : null
      }),
    [participantesPorFecha, campoCiudad?.key, configId],
  )

  const fieldOptionsFiltros = useMemo(() => {
    const out: Record<string, FieldOption[]> = {}
    const participantes = todosParticipantes.data ?? []

    // Extrae pares {id, nombre} únicos desde el listado de participantes.
    // - Provincia: top-level `provincia` (ID) + `provincia_nombre`.
    // - Cadena/Representante: en `datos_extra.<key>` (ID) + `<key>_nombre`.

    const provinciaPairs = collectPairs(
      participantes,
      (p) => p.provincia,
      (p) => p.provincia_nombre,
    )
    if (provinciaPairs.length > 0) out.provincia = provinciaPairs

    if (campoCadenaFiltro?.key) {
      const k = campoCadenaFiltro.key
      out[k] = collectPairs(
        participantes,
        (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[k],
        (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[`${k}_nombre`],
        true,
      )
    }
    if (campoRepFiltro?.key) {
      const k = campoRepFiltro.key
      out[k] = collectPairs(
        participantes,
        (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[k],
        (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[`${k}_nombre`],
      )
    }

    // Resto de campos de texto filtrables (ej. Razón social): dropdown con los
    // valores reales del listado en vez de un input de texto libre.
    const cubiertos = [
      'provincia',
      ...(campoCadenaFiltro?.key ? [campoCadenaFiltro.key] : []),
      ...(campoRepFiltro?.key ? [campoRepFiltro.key] : []),
    ]
    for (const [k, opts] of Object.entries(
      autoFieldOptions(participantes, camposExternos, cubiertos),
    )) {
      if (!out[k]) out[k] = opts
    }

    // Dependiente (reporte 7): dropdown de participantes registrados por nombre.
    if (configId === 7) {
      const pares = participantePairs(participantes)
      if (pares.length > 0) out.participante_id = pares
    }
    return out
  }, [todosParticipantes.data, campoCadenaFiltro?.key, campoRepFiltro?.key, camposExternos, configId])

  const fechaData = useMemo(
    () =>
      agruparConteo(participantesPorFecha, (p) =>
        p.created_at ? toLocalDate(String(p.created_at)) : null,
      )
        .map((d) => ({ fecha: d.label, total: d.value }))
        .sort((a, b) => a.fecha.localeCompare(b.fecha)),
    [participantesPorFecha],
  )

  const totalPart = derivarClientSide
    ? participantesFiltrados.length
    : resumen.data?.participantes.total ?? 0
  const validados = derivarClientSide
    ? participantesFiltrados.filter((p) => esVerificado(p)).length
    : resumen.data?.participantes.validados ?? 0
  const activos = derivarClientSide
    ? participantesFiltrados.filter((p) => p.activo === true || p.activo === 'true').length
    : resumen.data?.participantes.activos ?? 0
  const nuevos = useMemo(() => {
    const todos = todosParticipantes.data
    if (!todos) return resumen.data?.participantes.nuevos_en_rango ?? 0
    return todos.filter((p) => {
      if (validadoActivo) {
        const verifDeseado = filtros.validado === 'true'
        if (esVerificado(p) !== verifDeseado) return false
      }
      if (!pasaPuntos(p)) return false
      if (!pasaCascadaReps(p)) return false
      const extra = (p.datos_extra as Record<string, unknown> | undefined) ?? {}
      for (const [k, val] of Object.entries(filtros)) {
        if (!val || k === 'validado') continue
        const campoVal = k === 'provincia' ? p.provincia : k === 'participante_id' ? p.id : extra[k]
        // Filtro multi-selección (coma-separado): pasa si el valor del campo está
        // en el conjunto elegido.
        if (!parseMulti(val).includes(String(campoVal ?? ''))) return false
      }
      const f = p.created_at ? toLocalDate(String(p.created_at)) : ''
      return f >= rango.desde && f <= rango.hasta
    }).length
  }, [todosParticipantes.data, filtros, rango, validadoActivo, resumen.data, pasaCascadaReps, pasaPuntos])
  const pendientes = Math.max(totalPart - validados, 0)

  // Tabla client-side (sólo con el filtro "Puntos" activo): pagina localmente el
  // listado ya filtrado (participantesFiltrados incluye todos los filtros + puntos
  // + rango), para que totales y paginación reflejen el filtro por puntos.
  const PAGE_CS = 50
  const dataCS = useMemo<PaginatedListado>(() => {
    const total = participantesFiltrados.length
    const totalPages = Math.max(1, Math.ceil(total / PAGE_CS))
    const pageSafe = Math.min(pageCS, totalPages)
    return {
      items: participantesFiltrados.slice((pageSafe - 1) * PAGE_CS, pageSafe * PAGE_CS),
      page: pageSafe,
      page_size: PAGE_CS,
      total,
      total_pages: totalPages,
      has_next: pageSafe < totalPages,
      has_prev: pageSafe > 1,
    }
  }, [participantesFiltrados, pageCS])

  const totalCadenas = cadenaData.filter(d => d.label !== '(sin asignar)' && d.value > 0).length
  const totalRepresentantes = repData.filter(d => d.label !== '(sin asignar)' && d.value > 0).length
  const totalProvincias = provinciaData.filter(d => d.label !== '(sin asignar)' && d.value > 0).length
  const totalCiudades = ciudadData.filter(d => d.label !== '(sin asignar)' && d.value > 0).length

  // El nivel distrito solo está disponible en Medifarma Perú (id 7), que tiene
  // campo `ciudad` en el upstream y geojson de distritos.
  const hayDistritos = configId === 7 && campoCiudad != null && GEOJSON_DISTRITOS(configId) != null

  // El servidor filtra created_at por fechas UTC; registros de las últimas horas
  // del día en Perú (UTC-5) pueden quedar en el día siguiente UTC. Este filtro
  // client-side los excluye correctamente usando la fecha convertida a Perú.
  const rowFilterFecha = useMemo(() => {
    const { desde, hasta } = rangoEfectivo
    if (!desde && !hasta) return undefined
    return (row: Record<string, unknown>) => {
      const f = row.created_at ? toLocalDate(String(row.created_at)) : ''
      if (desde && f < desde) return false
      if (hasta && f > hasta) return false
      return true
    }
  }, [rangoEfectivo])

  return (
    <div className="space-y-6">
      <ReporteHeader
        config={config.data}
        programaNombre={metadata.data?.programa.nombre}
        icon={Users}
        seccion="Registros"
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      <ReporteSubNav
        configId={configId}
        colorNombre={color.nombre}
        colorTono={color.tono}
      />

      <ReportePeriodoBar
        rango={rango}
        mes={mes}
        onMesChange={setMes}
        minDate={metadata.data?.rango_datos?.primera_factura ?? null}
        maxDate={metadata.data?.rango_datos?.ultima_factura ?? null}
        configId={configId}
        colorNombre={color.nombre}
        exportTipos={[
          { tipo: 'participantes', label: 'Participantes' },
        ]}
        exportFiltros={filtrosInyectados}
        sinFecha={sinFecha}
        onSinFechaChange={setSinFecha}
        modo={modo}
        onModoChange={setModo}
        rangoPersonalizado={rangoPersonalizado}
        onRangoPersonalizadoChange={setRangoPersonalizado}
      />

      {/* Filtros globales del reporte: provincia / cadena / representante.
          Aplican a cards, charts, mapa y tabla. */}
      <DynamicFiltersBar
        configId={configId}
        metadata={metadata.data}
        value={filtros}
        onChange={handleFiltrosChange}
        hideDateRange
        omitDynamicFields={['provincia', 'nivel', 'email_verificado', 'activo']}
        fieldOptions={narrowRepOptions(fieldOptionsFiltros)}
        cascadeSelects={cascadeSelects}
        // Medifarma Perú (id 7): filtro "Verificado" ligado a la columna
        // "Email verificado". El upstream filtra ese campo por el param
        // `validado` (NO `email_verificado`, que ignora silenciosamente).
        boolFilters={configId === 7 ? [{ key: 'validado', label: 'Verificado' }] : undefined}
        // Filtro "Actividad" (id 7): activo = tiene puntos (>0); inactivo = ≤0.
        // Client-side (no se envía al upstream); afecta cards, gráficas, mapa y tabla.
        extraSelects={
          configId === 7
            ? [
                {
                  key: '__actividad',
                  label: 'Actividad',
                  value: filtroPuntos,
                  onChange: (v) => cambiarFiltroPuntos(v as FiltroPuntos),
                  options: [
                    { value: 'todos', label: 'Todos' },
                    { value: 'con', label: 'Activos' },
                    { value: 'sin', label: 'Inactivos' },
                  ],
                },
              ]
            : undefined
        }
      />

      {errorGlobal && (
        <Alert variant="destructive">
          <AlertDescription>{parseFidelizacionError(errorGlobal).message}</AlertDescription>
        </Alert>
      )}

      {/* KPI consolidado: Total registros + sub-stats */}
      <div className="grid gap-4 md:grid-cols-2">
        <StatCardConsolidado
          icon={Users}
          title="Total registros"
          value={totalPart}
          subStats={[
            { label: 'Validados', value: validados, tone: 'success' },
            { label: 'Pendientes', value: pendientes, tone: 'warning' },
          ]}
          colorNombre={color.nombre}
          colorTono={color.tono}
          configId={configId}
          isLoading={validadoActivo ? todosParticipantes.isLoading : resumen.isLoading}
        />
        <StatCardConsolidado
          icon={UserCheck}
          title="Actividad del mes"
          value={nuevos}
          subStats={[
            { label: 'Nuevos en el mes', value: nuevos, tone: 'default' },
            { label: 'Activos en total', value: activos, tone: 'muted' },
          ]}
          colorNombre={color.nombre}
          colorTono={color.tono}
          configId={configId}
          isLoading={todosParticipantes.isLoading}
        />
      </div>

      {/* Gráficas de barras (cadena / representante) a la izquierda; mapa al extremo derecho.
          Los totales únicos se muestran inline en el header de cada visualización. */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 items-stretch">
        {/* Medifarma Perú (id 7) no tiene datos de cadena; su desglose geográfico
            queda cubierto por el mapa, así que se omite esta gráfica. */}
        {configId !== 7 && (
          <BarChartLabelLeft
            title={campoCadena ? `Por ${campoCadena.label}` : 'Por cadena'}
            data={cadenaData}
            colorNombre={color.nombre}
            isLoading={todosParticipantes.isLoading}
            error={todosParticipantes.error}
            icon={Store}
            headerStat={{ value: totalCadenas, label: 'únicas' }}
            configId={configId}
          />
        )}
        <BarChartLabelLeft
          title={campoRepresentante ? `Por ${campoRepresentante.label}` : 'Por representante'}
          data={repData}
          colorNombre={color.nombre}
          isLoading={todosParticipantes.isLoading}
          error={todosParticipantes.error}
          icon={UserCheck}
          headerStat={{ value: totalRepresentantes, label: 'únicos' }}
          configId={configId}
        />
        {/* Medifarma Perú (id 7): tabs Departamento / Distrito dentro de la card.
            onActiveChange sincroniza el mapa con la tab activa. */}
        {hayDistritos && (
          <BarChartAgrupado
            title="Por área geográfica"
            colorNombre={color.nombre}
            icon={MapIcon}
            onActiveChange={(key) => setMostrarDistritos(key === 'distrito')}
            options={(
              [
                {
                  key: 'departamento',
                  label: 'Departamento',
                  icon: MapIcon,
                  data: provinciaData,
                  isLoading: todosParticipantes.isLoading,
                  error: todosParticipantes.error,
                  headerStat: { value: totalProvincias, label: 'únicos' },
                },
                {
                  key: 'distrito',
                  label: 'Distrito',
                  icon: MapPin,
                  data: ciudadData,
                  isLoading: todosParticipantes.isLoading,
                  error: todosParticipantes.error,
                  headerStat: { value: totalCiudades, label: 'únicos' },
                },
              ] satisfies DatasetOpcion[]
            )}
            configId={configId}
          />
        )}
        {/* Mapa único: alterna departamentos ↔ distritos con el botón de la
            cabecera. El nivel distrito solo está disponible en Medifarma Perú. */}
        <MapaRegiones
          title={
            mostrarDistritos
              ? 'Registros por distrito'
              : `Participantes por ${regionTermino(configId)}`
          }
          data={mostrarDistritos ? ciudadData : provinciaData}
          colorNombre={color.nombre}
          isLoading={todosParticipantes.isLoading}
          error={todosParticipantes.error}
          icon={MapIcon}
          headerStat={{
            value: mostrarDistritos ? totalCiudades : totalProvincias,
            label: 'únicas',
          }}
          geojsonUrl={
            mostrarDistritos ? GEOJSON_DISTRITOS(configId)! : GEOJSON_PAIS(configId)
          }
          insetName={mostrarDistritos ? null : insetPais(configId)}
          configId={configId}
        />
      </div>

      {/* Línea por fecha de registro */}
      <AreaChartFecha
        title="Registros por fecha"
        data={fechaData}
        colorNombre={color.nombre}
        colorTono={color.tono}
        isLoading={todosParticipantes.isLoading}
        configId={configId}
      />

      {/* Tabla con filtros — la barra de filtros se renderiza arriba a nivel
          de página (afecta cards/charts/mapa/tabla). Con el filtro "Puntos" activo
          la tabla se pagina client-side sobre el listado ya filtrado para que
          totales y paginación reflejen el filtro por puntos. */}
      {puntosActivo ? (
        <DynamicDataTable
          configId={configId}
          recurso="participantes"
          metadata={metadata.data}
          data={dataCS}
          isLoading={todosParticipantes.isLoading}
          isFetching={todosParticipantes.isFetching}
          page={dataCS.page}
          pageSize={PAGE_CS}
          onPageChange={setPageCS}
          omitCampos={configId === 7 ? ['provincia', 'cadena', 'ciudad', 'distrito'] : undefined}
        />
      ) : (
        <ListadoTab
          configId={configId}
          recurso="participantes"
          metadata={metadata.data}
          rango={rangoEfectivo}
          hideDateRange
          omitDynamicFields={['provincia', 'nivel', 'email_verificado', 'activo']}
          fieldOptions={fieldOptionsFiltros}
          filtros={filtrosInyectados}
          onFiltrosChange={setFiltros}
          hideFiltersBar
          rowFilter={rowFilterFecha}
          // El campo `provincia` (label "Departamento") duplica la columna fija
          // "Departamento"; en Medifarma Perú (id 7) tampoco se muestran "Cadena" ni
          // "Distrito" (campo `ciudad` del upstream).
          omitCampos={configId === 7 ? ['provincia', 'cadena', 'ciudad', 'distrito'] : undefined}
        />
      )}
    </div>
  )
}
